/**
 * 官网访问与行为打点。后台据此统计访客、设备、地区、停留、滚动、点击。
 *
 * 上报三类东西：
 *  1. 页面加载（/api/track）：访客 ID、会话 ID、UA 补充信息、屏幕参数、来源。
 *  2. 心跳（/api/track/batch）：页面可见且有操作时每 15 秒一次，带这段时间的活跃时长、最大滚动深度、看到过的板块。
 *  3. 事件（同一个接口，随心跳批量发）：点击（带元素名、板块、页面相对坐标）、板块到达、助手打开/提问。
 *
 * - 访客 ID（vid）存 localStorage，随机串，不含个人信息；会话 ID（sid）存 sessionStorage，30 分钟没动作就换新的。
 * - 元素名优先取 data-track 属性，没有的按 标签:文字 自动起名。
 * - 地址栏加 ?track=off 可临时关闭；file:// 离线单文件版不上报。
 */
import type { SiteConfig } from '../types/site'

const VID_KEY = 'rc_vid'
const SID_KEY = 'rc_sid'
const SID_TS_KEY = 'rc_sid_ts'
const SESSION_IDLE_MS = 30 * 60 * 1000
const HEARTBEAT_MS = 15_000
const IDLE_MS = 60_000            // 超过 1 分钟没有任何操作就不算活跃

function uuid(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) return crypto.randomUUID().replace(/-/g, '')
  return Array.from({ length: 32 }, () => Math.floor(Math.random() * 16).toString(16)).join('')
}

const ID_RE = /^[A-Za-z0-9_-]{8,64}$/

export function visitorId(): string {
  try {
    let v = localStorage.getItem(VID_KEY)
    if (!v || !ID_RE.test(v)) { v = uuid(); localStorage.setItem(VID_KEY, v) }
    return v
  } catch { return uuid() }
}

let memSid = ''
export function sessionId(): string {
  try {
    const now = Date.now()
    let s = sessionStorage.getItem(SID_KEY)
    const last = Number(sessionStorage.getItem(SID_TS_KEY) || 0)
    if (!s || !ID_RE.test(s) || now - last > SESSION_IDLE_MS) { s = uuid(); sessionStorage.setItem(SID_KEY, s) }
    sessionStorage.setItem(SID_TS_KEY, String(now))
    memSid = s
    return s
  } catch {
    if (!memSid) memSid = uuid()
    return memSid
  }
}

/* ------------------------------ 设备补充信息 ------------------------------ */
type UAData = { getHighEntropyValues?: (hints: string[]) => Promise<Record<string, unknown>>; platform?: string }

async function clientHints(): Promise<Record<string, string>> {
  const uad = (navigator as Navigator & { userAgentData?: UAData }).userAgentData
  if (!uad?.getHighEntropyValues) return {}
  try {
    const v = await uad.getHighEntropyValues(['model', 'platform', 'platformVersion'])
    return { model: String(v.model || ''), platform: String(v.platform || uad.platform || ''), platformVersion: String(v.platformVersion || '') }
  } catch { return { platform: uad.platform || '' } }
}

function gpuName(): string {
  try {
    const canvas = document.createElement('canvas')
    const gl = (canvas.getContext('webgl') || canvas.getContext('experimental-webgl')) as WebGLRenderingContext | null
    if (!gl) return ''
    const ext = gl.getExtension('WEBGL_debug_renderer_info')
    const name = ext ? String(gl.getParameter(ext.UNMASKED_RENDERER_WEBGL)) : ''
    gl.getExtension('WEBGL_lose_context')?.loseContext()
    return name.slice(0, 120)
  } catch { return '' }
}

/* ------------------------------ 状态 ------------------------------ */
type TrackEvent = { t: number; kind: 'click' | 'section' | 'assistant' | 'custom'; name: string; x?: number; y?: number; vw?: number; section?: string; meta?: Record<string, unknown> }

const state = {
  enabled: false,
  endpoint: '/assistant/api/track',
  vid: '',
  sid: '',
  path: '',
  locale: '',
  queue: [] as TrackEvent[],
  activeMs: 0,
  lastTick: 0,
  lastInput: 0,
  maxScroll: 0,
  sections: new Set<string>(),
  sectionsSent: new Set<string>(),
  timer: 0,
  started: false,
}

function post(url: string, body: string, beacon = true): void {
  try {
    if (beacon && navigator.sendBeacon && navigator.sendBeacon(url, new Blob([body], { type: 'application/json' }))) return
  } catch { /* 走 fetch */ }
  fetch(url, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body, keepalive: true, credentials: 'omit' }).catch(() => {})
}

/* ------------------------------ 页面加载 ------------------------------ */
let sent = false

export async function trackVisit(config: SiteConfig | null, locale: string): Promise<void> {
  if (sent || typeof window === 'undefined') return
  if (window.location.protocol === 'file:') return
  if (new URLSearchParams(window.location.search).get('track') === 'off') return
  if (config?.analytics?.enabled === false) return
  sent = true

  state.endpoint = config?.analytics?.endpoint || '/assistant/api/track'
  state.vid = visitorId()
  state.sid = sessionId()
  state.path = window.location.pathname
  state.locale = locale
  state.enabled = true

  const ch = await clientHints()
  post(state.endpoint, JSON.stringify({
    vid: state.vid,
    sid: state.sid,
    path: window.location.pathname + window.location.search,
    ref: document.referrer || '',
    screen: { w: window.screen.width, h: window.screen.height, dpr: window.devicePixelRatio || 1 },
    touch: navigator.maxTouchPoints || 0,
    ch,
    gpu: gpuName(),
    locale,
    lang: navigator.language || '',
  }))

  startEngagement()
}

/* ------------------------------ 行为 ------------------------------ */
function sectionNameOf(el: Element | null): string {
  let cur: Element | null = el
  while (cur && cur !== document.body) {
    const named = (cur as HTMLElement).dataset?.trackSection
    if (named) return named
    if (cur.tagName === 'SECTION' || cur.tagName === 'FOOTER' || cur.tagName === 'HEADER') {
      return cur.id || (cur.className && String(cur.className).split(/\s+/)[0]) || cur.tagName.toLowerCase()
    }
    cur = cur.parentElement
  }
  return ''
}

function nameOf(target: Element): string {
  // 先找带 data-track 的祖先（比如门店缩略图按钮），再退到最近的可交互元素
  const named = target.closest<HTMLElement>('[data-track]')
  if (named?.dataset.track) return named.dataset.track
  const el = target.closest<HTMLElement>('a, button, [role="button"], input, select, textarea, label, img, video')
  const inAssistant = !!target.closest('.ora')
  // 没点到任何可交互元素：手机上滑动时的误触、点空白，统一记成 blank，后台按板块显示
  if (!el) return inAssistant ? 'assistant:panel' : 'blank'
  const tag = el.tagName.toLowerCase()
  const text = (el.getAttribute('aria-label') || el.getAttribute('title') || el.textContent || '').replace(/\s+/g, ' ').trim().slice(0, 30)
  if (inAssistant) return `assistant:${text || tag}`
  if (tag === 'video') return 'video'
  if (text) return `${tag}:${text}`
  if (tag === 'a') return `a:${(el as HTMLAnchorElement).getAttribute('href') || ''}`.slice(0, 60)
  if (tag === 'img') return `img:${((el as HTMLImageElement).alt || (el as HTMLImageElement).src.split('/').pop() || '').slice(0, 30)}`
  return tag
}

function pageHeight(): number {
  const d = document.documentElement
  return Math.max(d.scrollHeight, document.body?.scrollHeight || 0, 1)
}

function onClick(e: MouseEvent): void {
  if (!state.enabled) return
  const target = e.target as Element | null
  if (!target) return
  state.lastInput = Date.now()
  const docY = (e.clientY + window.scrollY) / pageHeight() * 100
  state.queue.push({
    t: Date.now(), kind: 'click', name: nameOf(target),
    x: e.clientX / Math.max(window.innerWidth, 1) * 100, y: docY, vw: window.innerWidth,
    section: sectionNameOf(target),
  })
  if (state.queue.length >= 20) flush()
}

function onScroll(): void {
  state.lastInput = Date.now()
  const h = pageHeight() - window.innerHeight
  const pct = h <= 0 ? 100 : Math.min(100, Math.round((window.scrollY / h) * 100))
  if (pct > state.maxScroll) state.maxScroll = pct
}

function observeSections(): void {
  if (!('IntersectionObserver' in window)) return
  const nodes = [...document.querySelectorAll<HTMLElement>('main > section, main section[id], main > div > section, footer, [data-track-section]')]
    .filter(n => !n.closest('.ora'))     // 助手 widget 自己的结构不算板块
  const io = new IntersectionObserver((entries) => {
    for (const en of entries) {
      if (!en.isIntersecting) continue
      const name = sectionNameOf(en.target)
      if (!name || state.sectionsSent.has(name) || state.sections.has(name)) continue
      state.sections.add(name)
      state.queue.push({ t: Date.now(), kind: 'section', name })
    }
  }, { threshold: 0.35 })
  nodes.forEach(n => io.observe(n))
}

function tick(): void {
  const now = Date.now()
  const visible = document.visibilityState === 'visible'
  const active = visible && now - state.lastInput < IDLE_MS
  if (active && state.lastTick) state.activeMs += Math.min(now - state.lastTick, HEARTBEAT_MS * 2)
  state.lastTick = now
  if (active || state.queue.length) flush()
}

function flush(final = false): void {
  if (!state.enabled) return
  if (!state.queue.length && state.activeMs < 1000 && !final) return
  const sections = [...state.sections].filter(s => !state.sectionsSent.has(s))
  const body = JSON.stringify({
    vid: state.vid, sid: state.sid, path: state.path,
    activeMs: Math.round(state.activeMs), maxScroll: state.maxScroll, sections,
    events: state.queue.splice(0, 60),
  })
  state.activeMs = 0
  sections.forEach(s => state.sectionsSent.add(s))
  try { sessionStorage.setItem(SID_TS_KEY, String(Date.now())) } catch { /* ignore */ }
  post(state.endpoint + '/batch', body)
}

function startEngagement(): void {
  if (state.started) return
  state.started = true
  const now = Date.now()
  state.lastTick = now
  state.lastInput = now
  onScroll()

  document.addEventListener('click', onClick, { capture: true, passive: true })
  window.addEventListener('scroll', onScroll, { passive: true })
  for (const ev of ['keydown', 'touchstart', 'pointerdown', 'mousemove'] as const) {
    window.addEventListener(ev, () => { state.lastInput = Date.now() }, { passive: true })
  }
  // 助手 widget 发来的事件
  window.addEventListener('rc:track', (e: Event) => {
    const d = (e as CustomEvent).detail || {}
    if (!d.name) return
    state.lastInput = Date.now()
    state.queue.push({ t: Date.now(), kind: d.kind === 'assistant' ? 'assistant' : 'custom', name: String(d.name).slice(0, 80), meta: d.meta })
  })
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') { tick(); flush(true) } else { state.lastTick = Date.now(); state.lastInput = Date.now() }
  })
  window.addEventListener('pagehide', () => { tick(); flush(true) })

  state.timer = window.setInterval(tick, HEARTBEAT_MS)
  // 板块等 Vue 渲染完再挂观察器
  window.setTimeout(observeSections, 800)
}

/** 给业务代码手动记事件用，例如 track('video-play') */
export function track(name: string, meta?: Record<string, unknown>): void {
  if (!state.enabled) return
  state.queue.push({ t: Date.now(), kind: 'custom', name: name.slice(0, 80), meta })
}
